import {
  Box,
  Button,
  Card,
  CardCover,
  Checkbox,
  Divider,
  FormControl,
  FormHelperText,
  FormLabel,
  Grid,
  IconButton,
  Input,
  Option,
  Radio,
  RadioGroup,
  Select,
  Sheet,
  Stack,
  Typography,
} from "@mui/joy";
import { FormProvider } from "react-hook-form";
import React, { useEffect, useMemo, useState } from "react";
import { CustomICreateBusiness, IUpdateBusiness } from "../../types/business";
import { useTranslation } from "react-i18next";
import { If, IfElse } from "../../components/Condition";
import NotFound from "../NotFound/NotFound";
import { theme } from "../../components/Theme";
import useContainer from "./useContainer";
import {
  ArrowUpTrayIcon,
  BuildingOffice2Icon,
  ExclamationCircleIcon,
  TrashIcon,
  TruckIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import PhoneInput from "react-phone-input-2";
import {
  CountryCode,
  DefaultCurrencies,
  PhNoFormat,
  PhNoWhiteListedCountries,
  WhiteListedCountries,
} from "../../utils/constants";
import { BusinessSessionHelper } from "../../helpers/business-session.helper";
import startsWith from "lodash.startswith";
import { matchesFormat } from "../../utils/misc";
import FlagById from "../../components/FlagById";
import { countries } from "../../components/Countries";
import { animateSelectStyle } from "../../styles/select";
import { KeyboardArrowDown } from "@mui/icons-material";
import { PolicyBullet } from "../../components/PasswordPolicy/PolicyBullet";
import { useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeftIcon,
  ArrowRightIcon,
  CheckIcon,
} from "@heroicons/react/24/solid";
import CurrencyDolar from "../../components/Icons/CurrencyDolar";
import CoinStack from "../../components/Icons/CoinStack";
import {
  APPLICABLE_ADMIN_FEE_OPTIONS,
  ApplicableAdminFeeTypes,
} from "../../types/applicable-admin-fee.enum";
import { SnackBarInfo } from "../../components/snackbar";
import Puzzle from "../../components/Icons/Puzzle";
import { TaxItem } from "../Profile/Business/TaxItem";
import { findBankOptions } from "../../utils/bank-options";
import { currencyList } from "../Order/EditOrders";
import { getCountryInfo } from "../../utils/country";
import {
  findAverageValueOfTransactionsByCountry,
  findEstimatedAnualRevenueOptionsByCountry,
} from "../../utils/kyc-form-options";
import { UploadService } from "../../services/upload.service";
import LoadingModal from "../../components/LoadingModal/LoadingModal";
import ConfirmUpgradeModal from "./components/Form/ConfirmUpgradeModal";
import FormStepper from "../../components/FormStepper";

// TODO: this page needs to be refactored, trimmed to make easier to read
const UpgradeForm = () => {
  const { t } = useTranslation();

  const STEPS = [
    {
      step: 1,
      title: t("upgradeAccount.form.steps.1.name"),
    },
    {
      step: 2,
      title: t("upgradeAccount.form.steps.2.name"),
    },
    {
      step: 3,
      title: t("upgradeAccount.form.steps.3.name"),
    },
  ];

  const navigate = useNavigate();

  const { businessId } = useParams();

  const {
    methods,
    companyIcon,
    setCompanyIcon,
    setUploadedCompanyPicture,
    getBusiness,
    accountBusiness,
    setAccountBusiness,
    selectedTaxes,
    setSelectedTaxes,
    defaultTaxId,
    setDefaultTaxId,
    isLoading,
    setIsLoading,
    companyProfileFile,
    personalIDFile,
    setCompanyProfileFile,
    setPersonalIDFile,
    setWithKyc,
    taxOptions,
    defaultCurrency,
    imgExistanceCheckQuery,
    updateMutation,
  } = useContainer();

  const [currentStep, setCurrentStep] = useState(
    accountBusiness ? accountBusiness.step : 1
  );
  const [isStepValid, setIsStepValid] = useState(false);
  const BANK_OPTIONS = findBankOptions(
    BusinessSessionHelper.Business()?.countryCode ?? "ID"
  );
  const ESTIMATED_ANUAL_REVENUE = findEstimatedAnualRevenueOptionsByCountry(
    BusinessSessionHelper.Business()?.countryCode ?? "ID"
  );
  const AVERAGE_VALUE_OF_TRANSACTIONS = findAverageValueOfTransactionsByCountry(
    BusinessSessionHelper.Business()?.countryCode ?? "ID"
  );
  const [isAcceptTerms, setIsAcceptTerms] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  const {
    handleSubmit,
    formState: { errors },
    register,
    getValues,
    setValue,
    trigger,
    setError,
    clearErrors,
    watch,
  } = methods;

  const watchValidation =
    currentStep === 1
      ? watch([
          "companyName",
          "companyNumber",
          "companyNumberCountryCode",
          "companyWebsite",
          "companyCountry",
          "companyProvince",
          "companyAddress",
          "companyAddress2",
          "companyPostalCode",
        ])
      : currentStep === 2
      ? watch([
          "companyCurrency",
          "companyBankName",
          "companyBankAccountNo",
          "companyBankAccountHolderName",
          "applicableAdminFee",
        ])
      : watch([
          "estimatedAnualIncomeCurrency",
          "estimatedAnualIncome",
          "averageValuePerTransactionCurrency",
          "averageValuePerTransaction",
          "bankName",
          "accountNumber",
          "companyProfileFileUrl",
          "personalIdFileUrl",
        ]);

  /**
   * validate step every time watchValidation changes
   */
  useEffect(() => {
    setIsStepValid(handleValidateStep(currentStep));
  }, [watchValidation]);

  /** fetch business and reset form */
  useEffect(() => {
    if (businessId && !accountBusiness) {
      getBusiness(businessId);
    }
  }, [businessId]);

  useEffect(() => {
    initDragDropListener(
      ".drop-zone-business-profile-file",
      (file) => {
        setCompanyProfileFile(file);
        setError("companyProfileFileUrl", {});
      },
      () =>
        setError("companyProfileFileUrl", {
          message: "File type not supported",
        })
    );
    initDragDropListener(
      ".drop-zone-personal-id-file",
      (file) => {
        setPersonalIDFile(file);
        setError("personalIdFileUrl", {});
      },
      () =>
        setError("personalIdFileUrl", {
          message: "File type not supported",
        })
    );
  }, []);

  const companyIconBlobUrl = useMemo(() => {
    if (!companyIcon) return null;
    return URL.createObjectURL(companyIcon);
  }, [companyIcon]);

  useEffect(() => {
    return () => {
      if (companyIconBlobUrl) {
        URL.revokeObjectURL(companyIconBlobUrl);
      }
    };
  }, [companyIconBlobUrl]);

  const initDragDropListener = (
    zoneClassName: string,
    fileHandler: (file: File | null) => void,
    errorHandler: () => void
  ) => {
    const dropZone = document.querySelector(zoneClassName);
    dropZone?.addEventListener("dragover", (e) => {
      e.preventDefault();
      dropZone?.setAttribute("style", "background: #f2f2f2;");
      console.log("Drag", e);
    });
    dropZone?.addEventListener("dragleave", () => {
      dropZone?.setAttribute("style", "background: #ffffff;");
    });
    dropZone?.addEventListener("drop", (e) => {
      e.preventDefault();
      dropZone?.setAttribute("style", "background: #ffffff;");
      console.log("Drop", e);
      onFileChange(e, (file: File) => {
        if (
          !["image/jpeg", "image/png", "application/pdf"].includes(file.type)
        ) {
          errorHandler();
          return;
        }
        fileHandler(file);
      });
    });
  };

  const onFileChange = (e: any, handler?: (file: File) => void) => {
    const files = e.target?.files || e.dataTransfer?.files;
    console.log("FILE", files);
    if ((files?.length ?? 0) > 0) {
      handler?.(files[0]);
    }
  };

  const onSubmit = (data: any) => {
    console.log("SUBMITTED DATA", data);

    if (!businessId) {
      return;
    }

    setIsLoading(true);

    if (companyIcon) {
      UploadService.upload(companyIcon, businessId).finally(() => {
        setIsLoading(false);
        imgExistanceCheckQuery.refetch();
        updateMutation.mutate({
          businessId: businessId,
          payload: data as IUpdateBusiness,
        });
      });
    } else {
      updateMutation.mutate({
        businessId: businessId,
        payload: data as IUpdateBusiness,
      });
    }
  };

  /**
   * validate each step items before proceed
   * @param step number
   * @returns boolean
   */
  const handleValidateStep = (step: number) => {
    let result = false;
    switch (step) {
      case 1:
        const fieldsToValidate = getValues([
          "companyName",
          "companyNumber",
          "companyNumberCountryCode",
          "companyCountry",
          "companyProvince",
          "companyAddress",
          "companyPostalCode",
        ]);

        console.log("VALIDATE FORM 1", fieldsToValidate);

        // make sure all fields are filled
        const areFieldsValid =
          !fieldsToValidate.some((field) => field === undefined) &&
          !fieldsToValidate.includes("");

        result = areFieldsValid;
        break;

      case 2:
        const fieldsToValidate2 = getValues([
          "companyCurrency",
          "companyBankName",
          "companyBankAccountNo",
          "companyBankAccountHolderName",
          "applicableAdminFee",
        ]);

        // make sure all fields are filled
        const areFieldsValid2 =
          !fieldsToValidate2.some((field) => field === undefined) &&
          !fieldsToValidate2.includes("");

        result = areFieldsValid2;
        break;

      case 3:
        const fieldsToValidate3 = getValues([
          "estimatedAnualIncomeCurrency",
          "estimatedAnualIncome",
          "averageValuePerTransactionCurrency",
          "averageValuePerTransaction",
          "bankName",
          "accountNumber",
        ]);

        // make sure all fields are filled
        const areFieldsValid3 =
          !fieldsToValidate3.some((field) => field === undefined) &&
          !fieldsToValidate3.includes("");

        result = areFieldsValid3;
        break;

      default:
        result = false;
        break;
    }

    return result;
  };

  /**
   * save current changes to localStorages
   * @param changes
   */
  const handleSaveData = (changes?: any) => {
    const data = getValues();
    const savePayload = {
      ...accountBusiness,
      ...data,
      ...changes,
    };

    console.log("SAVE PAYLOAD", savePayload);

    setAccountBusiness(savePayload as CustomICreateBusiness);

    localStorage.setItem("upgrade-form-cache", JSON.stringify(savePayload));
  };

  /**
   * save changes to localStorage, proceed to next step
   */
  const handleGoToNext = () => {
    if (!handleValidateStep(currentStep)) {
      return;
    }

    handleSaveData({ step: currentStep + 1 });
    setCurrentStep((prev) => prev + 1);
  };

  /**
   * save changes to localStorage, go back to previous page
   */
  const handleGotoPrev = () => {
    handleSaveData();
    setCurrentStep((prev) => prev - 1);
  };

  /**
   * Handles file change event from profile picture input.
   * It will update the display picture URL.
   * @param {any} e the event object
   */
  const handleFileChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    handler: (file: File) => void
  ) => {
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0];
      handler(file);
    }
  };

  if (!businessId) {
    return <NotFound />;
  }

  return (
    <Sheet
      sx={{
        backgroundColor: "transparent",
        p: { xs: 0, md: "32px" },
        pb: 0,
      }}
    >
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Stack direction={"column"} gap={"32px"}>
            {/* steps */}
            <FormStepper
              currentStep={currentStep}
              validation={handleValidateStep}
              onClick={(_step: number) => {}}
              steps={STEPS}
            />

            {/* company info */}
            <If condition={currentStep === 1}>
              <Box
                sx={{
                  borderWidth: "1px",
                  borderColor: theme.palette.neutral[300],
                  borderRadius: "8px",
                  py: "32px",
                  px: "24px",
                  display: "flex",
                  flexDirection: "column",
                  gap: "32px",
                }}
              >
                <Typography
                  level="text-xl2-bold"
                  sx={{
                    color: theme.palette.others.onboarding_create_account_font,
                  }}
                >
                  {t("upgradeAccount.form.steps.1.title")}
                </Typography>

                {/* change company picture */}
                <Stack
                  direction={{ xs: "column", md: "row" }}
                  sx={{
                    justifyContent: "space-between",
                    alignItems: "center",
                    width: "100%",
                    gap: "8px",
                  }}
                >
                  <Stack
                    direction={"row"}
                    sx={{
                      gap: "8px",
                      alignItems: "center",
                    }}
                  >
                    <Card
                      sx={{
                        width: 100,
                        height: 100,
                        borderRadius: "8px",
                      }}
                    >
                      <CardCover>
                        <img
                          id="companyIcon"
                          src={companyIconBlobUrl ?? "/upload-placeholder.png"}
                        />
                      </CardCover>
                    </Card>

                    <CustomImagePolicy file={companyIcon} />
                  </Stack>

                  <Button
                    variant="linkz-outlined-primary"
                    sx={{
                      mr: { xs: "auto", md: 0 },
                      ml: { xs: 0, md: "auto" },
                    }}
                    onClick={() =>
                      document
                        .getElementById("display-picture-company")
                        ?.click()
                    }
                  >
                    {t("commons.uploadBtn")}
                  </Button>
                  <input
                    id="display-picture-company"
                    accept="image/*"
                    type="file"
                    style={{ display: "none" }}
                    onChange={(e) =>
                      handleFileChange(e, (file) => {
                        setUploadedCompanyPicture(file);
                        setCompanyIcon(file);
                      })
                    }
                  />
                </Stack>

                <Typography
                  level="title-md"
                  startDecorator={<BuildingOffice2Icon width={20} />}
                >
                  {t("profile.editProfile.companyInfo.title")}
                </Typography>

                {/* company details */}
                <Grid
                  container
                  spacing={2}
                  sx={{
                    width: "100%",
                  }}
                >
                  {/* companyName */}
                  <Grid xs={12}>
                    <FormControl error={!!errors.companyName}>
                      <FormLabel>
                        {t("profile.editProfile.companyInfo.companyName")}
                        <span className="text-danger-500">*</span>
                      </FormLabel>
                      <Input {...register("companyName", { required: true })} />
                      {errors.companyName?.message && (
                        <FormHelperText>
                          {errors.companyName?.message}
                        </FormHelperText>
                      )}
                    </FormControl>
                  </Grid>

                  {/* companyWebsite */}
                  <Grid xs={12}>
                    <FormControl error={!!errors.companyWebsite}>
                      <FormLabel>
                        {t("profile.editProfile.companyInfo.companyWebsite")}
                      </FormLabel>
                      <Input {...register("companyWebsite")} />
                      {errors.companyWebsite?.message && (
                        <FormHelperText>
                          {errors.companyWebsite?.message}
                        </FormHelperText>
                      )}
                    </FormControl>
                  </Grid>

                  <Grid
                    xs={12}
                    sx={{
                      display: { xs: "flex", md: "none" },
                      mt: "32px",
                    }}
                  >
                    <Typography
                      level="title-md"
                      startDecorator={<TruckIcon width={20} />}
                    >
                      {t("profile.editProfile.companyAddress.title")}
                    </Typography>
                  </Grid>

                  {/* companyCountry & companyPhoneNumber */}
                  <Grid xs={12} md={6}>
                    <FormControl error={!!errors.companyCountry}>
                      <FormLabel>{t("profile.address.country")}</FormLabel>
                      <Select
                        placeholder={t("profile.address.country")}
                        indicator={<KeyboardArrowDown />}
                        onChange={(_, value) => {
                          if (value) setValue("companyCountry", value);
                        }}
                        renderValue={(option) => (
                          <Typography
                            startDecorator={
                              <FlagById id={`${option?.value ?? ""}`} />
                            }
                          >
                            {option?.label}
                          </Typography>
                        )}
                        value={watch("companyCountry")}
                        sx={{
                          ...animateSelectStyle,
                          backgroundColor: "white",
                        }}
                      >
                        {countries
                          .filter((country) =>
                            WhiteListedCountries.includes(country.code)
                          )
                          .map((country) => (
                            <Option
                              key={country.code}
                              value={country.code}
                              label={country.label}
                            >
                              <Typography
                                startDecorator={<FlagById id={country.code} />}
                              >
                                {country.label}
                              </Typography>
                            </Option>
                          ))}
                      </Select>
                      {errors.companyCountry?.message && (
                        <FormHelperText>
                          {errors.companyCountry?.message}
                        </FormHelperText>
                      )}
                    </FormControl>
                  </Grid>
                  <Grid xs={12} md={6}>
                    <FormControl error={!!errors.companyNumber}>
                      <FormLabel>
                        {t("profile.address.phoneNumber")}
                        <span className="text-danger-500">*</span>
                      </FormLabel>
                      <PhoneInput
                        value={
                          (watch("companyNumber") &&
                            `+${watch("companyNumberCountryCode")}${watch(
                              "companyNumber"
                            )}`) ||
                          ""
                        }
                        onlyCountries={PhNoWhiteListedCountries}
                        enableTerritories={false}
                        country={BusinessSessionHelper.GetBusinessCountryID().toLocaleLowerCase()}
                        masks={{
                          my: "..-....-....",
                          sg: "....-....",
                          th: "... ... ...",
                          id: "... ... ... ...",
                          mm: ". .... .... .....",
                          au: "... ... ...",
                        }}
                        isValid={(inputNumber, meta: any, countries) => {
                          if (
                            inputNumber === "" ||
                            inputNumber === meta.dialCode
                          ) {
                            return true;
                          }

                          if (
                            inputNumber
                              .toString()
                              .startsWith(`${meta.dialCode}0`)
                          ) {
                            if (!errors.companyNumber?.message) {
                              setError("companyNumber", {
                                message: t(
                                  "commons.error.form.mobileInvalidStartZero"
                                ),
                              });
                            }
                            return false;
                          }

                          const result = (countries || []).some(
                            (country: any) => {
                              if (
                                startsWith(inputNumber, country.dialCode) ||
                                startsWith(country.dialCode, inputNumber)
                              ) {
                                const iso2: CountryCode =
                                  meta.iso2 as CountryCode;
                                const format = PhNoFormat[iso2];
                                if (!format) return false;
                                return matchesFormat(
                                  inputNumber,
                                  format,
                                  country.countryCode
                                );
                              }
                              return false;
                            }
                          );

                          if (!result && !errors.companyNumber?.message) {
                            setError("companyNumber", {
                              message: t("commons.error.form.mobileInvalid"),
                            });
                          } else if (
                            result &&
                            !!errors.companyNumber?.message
                          ) {
                            if (errors.companyNumber.type === "manual") {
                              return false;
                            }
                            clearErrors("companyNumber");
                            trigger("companyNumber");
                          }
                          return result;
                        }}
                        inputStyle={{
                          width: "100%",
                          fontSize: "16px",
                          minHeight: "2.8rem",
                          height: "auto",
                          borderRadius: "8px",
                          fontWeight: 500,
                        }}
                        buttonStyle={{
                          borderRadius: "8px 0 0 8px",
                        }}
                        onChange={(phone, meta: any) => {
                          setValue("companyNumberCountryCode", meta.dialCode);
                          setValue(
                            "companyNumber",
                            phone.replace(new RegExp(meta.dialCode), "")
                          );
                        }}
                      />
                      {errors.companyNumber?.message && (
                        <FormHelperText>
                          {errors.companyNumber?.message}
                        </FormHelperText>
                      )}
                    </FormControl>
                  </Grid>

                  {/* companyAddress */}
                  <Grid xs={12}>
                    <FormControl error={!!errors.companyAddress}>
                      <FormLabel>
                        {t("profile.editProfile.personalAddress.addressLine1")}
                        <span className="text-danger-500">*</span>
                      </FormLabel>
                      <Input
                        {...register("companyAddress", { required: true })}
                      />
                      {errors.companyAddress?.message && (
                        <FormHelperText>
                          {errors.companyAddress?.message}
                        </FormHelperText>
                      )}
                    </FormControl>
                  </Grid>

                  {/* companyAddress2 */}
                  <Grid xs={12}>
                    <FormControl error={!!errors.companyAddress2}>
                      <FormLabel>
                        {t("profile.editProfile.personalAddress.addressLine2")}
                      </FormLabel>
                      <Input {...register("companyAddress2")} />
                      {errors.companyAddress2?.message && (
                        <FormHelperText>
                          {errors.companyAddress2?.message}
                        </FormHelperText>
                      )}
                    </FormControl>
                  </Grid>

                  {/* companyProvince & companyPostalCode */}
                  <Grid xs={12} md={6}>
                    <FormControl error={!!errors.companyProvince}>
                      <FormLabel>
                        {t("profile.address.provinceState")}
                        <span className="text-danger-500">*</span>
                      </FormLabel>
                      <Input
                        placeholder={t("profile.address.typeProvinceState")}
                        {...register("companyProvince")}
                      />
                      {errors.companyProvince?.message && (
                        <FormHelperText>
                          {errors.companyProvince?.message}
                        </FormHelperText>
                      )}
                    </FormControl>
                  </Grid>
                  <Grid xs={12} md={6}>
                    <FormControl error={!!errors.companyPostalCode}>
                      <FormLabel>{t("profile.address.postalCode")}</FormLabel>
                      <Input
                        placeholder={t("profile.address.typePostalCode")}
                        {...register("companyPostalCode")}
                      />
                      {errors.companyPostalCode?.message && (
                        <FormHelperText>
                          {errors.companyPostalCode?.message}
                        </FormHelperText>
                      )}
                    </FormControl>
                  </Grid>
                </Grid>
              </Box>
            </If>

            {/* payment info */}
            <If condition={currentStep === 2}>
              <Box
                sx={{
                  borderWidth: "1px",
                  borderColor: theme.palette.neutral[300],
                  borderRadius: "8px",
                  py: "32px",
                  px: "24px",
                  display: "flex",
                  flexDirection: "column",
                  gap: "32px",
                }}
              >
                <Typography
                  level="text-xl2-bold"
                  sx={{
                    color: theme.palette.others.onboarding_create_account_font,
                  }}
                >
                  {t("upgradeAccount.form.steps.2.title")}
                </Typography>

                <Typography
                  startDecorator={<CurrencyDolar />}
                  fontWeight={"lg"}
                >
                  {t("profile.business.paymentInfo")}
                </Typography>

                <Stack
                  direction={{ xs: "column", lg: "row" }}
                  sx={{
                    gap: "16px",
                  }}
                >
                  <FormControl error={!!errors.companyCurrency}>
                    <FormLabel>
                      {t("profile.business.companyCurrency")}
                    </FormLabel>
                    <Select
                      placeholder={t("profile.business.companyCurrency")}
                      indicator={<KeyboardArrowDown />}
                      onChange={(_, value) => {
                        if (value) setValue("companyCurrency", value);
                      }}
                      value={watch("companyCurrency") ?? defaultCurrency?.id}
                      sx={{
                        ...animateSelectStyle,
                        backgroundColor: "white",
                      }}
                    >
                      {DefaultCurrencies.map((currency) => (
                        <Option key={currency.id} value={currency.id}>
                          {currency.label}
                        </Option>
                      ))}
                    </Select>
                    {errors.companyCurrency?.message && (
                      <FormHelperText>
                        {errors.companyCurrency?.message}
                      </FormHelperText>
                    )}
                  </FormControl>

                  <FormControl
                    sx={{ flex: 1 }}
                    error={!!errors.companyBankName}
                  >
                    <FormLabel>
                      {t("profile.business.companyBankName")}
                    </FormLabel>
                    <Input
                      placeholder={t("profile.business.companyBankName")}
                      sx={{ backgroundColor: "white" }}
                      {...register("companyBankName")}
                    />
                    {errors.companyBankName?.message && (
                      <FormHelperText>
                        {errors.companyBankName?.message}
                      </FormHelperText>
                    )}
                  </FormControl>
                </Stack>

                <FormControl error={!!errors.companyBankAccountNo}>
                  <FormLabel>
                    {t("profile.business.companyBankAccountNo")}
                  </FormLabel>
                  <Input
                    placeholder={t("profile.business.companyBankAccountNo")}
                    sx={{ backgroundColor: "white" }}
                    {...register("companyBankAccountNo")}
                  />
                  {errors.companyBankAccountNo?.message && (
                    <FormHelperText>
                      {errors.companyBankAccountNo?.message}
                    </FormHelperText>
                  )}
                </FormControl>

                <FormControl error={!!errors.companyBankAccountHolderName}>
                  <FormLabel>
                    {t("profile.business.companyBankAccountHolderName")}
                  </FormLabel>
                  <Input
                    placeholder={t(
                      "profile.business.companyBankAccountHolderName"
                    )}
                    sx={{ backgroundColor: "white" }}
                    {...register("companyBankAccountHolderName")}
                  />
                  {errors.companyBankAccountHolderName?.message && (
                    <FormHelperText>
                      {errors.companyBankAccountHolderName?.message}
                    </FormHelperText>
                  )}
                </FormControl>

                {/* Admin Fee */}
                <Typography startDecorator={<CoinStack />} fontWeight={"lg"}>
                  {t("profile.business.adminFee")}
                </Typography>
                <FormControl error={!!errors.applicableAdminFee}>
                  <Card>
                    <Stack gap={1}>
                      <SnackBarInfo message="Choose who pays the admin fee. By default, it's charged to the Seller." />
                      <RadioGroup
                        value={
                          watch("applicableAdminFee") ??
                          ApplicableAdminFeeTypes.CHARGE_TO_SELLER
                        }
                        onChange={(_event) =>
                          setValue("applicableAdminFee", _event.target.value)
                        }
                        sx={{ display: "flex", gap: 1 }}
                      >
                        {APPLICABLE_ADMIN_FEE_OPTIONS.map((option) => (
                          <Radio
                            sx={{ padding: 1 }}
                            value={option.value}
                            label={t(option.labelKey)}
                          />
                        ))}
                      </RadioGroup>
                    </Stack>
                  </Card>
                  <FormHelperText>
                    {errors.applicableAdminFee?.message}
                  </FormHelperText>
                </FormControl>

                {/* Payment Terms */}
                <Typography startDecorator={<Puzzle />} fontWeight={"lg"}>
                  {t("profile.business.paymentTerms")}
                </Typography>
                {(watch("companyPaymentTerms") ?? []).map((term, index) => (
                  <div key={index}>
                    <Stack direction={"row"} justifyContent={"space-between"}>
                      <Typography key={index} fontWeight={"lg"}>
                        {t("profile.business.paymentTerms")} {index + 1}
                      </Typography>
                      <Button
                        color="danger"
                        variant="plain"
                        onClick={() => {
                          setValue(
                            "companyPaymentTerms",
                            watch("companyPaymentTerms")?.filter(
                              (_, i) => i !== index
                            ) ?? []
                          );
                        }}
                      >
                        {t("profile.business.removePaymentTrmBtn")}
                      </Button>
                    </Stack>
                    <FormControl key={index}>
                      <FormLabel>
                        {t("profile.business.paymentTerms")}
                      </FormLabel>
                      <Input
                        placeholder={t("profile.business.paymentTerms")}
                        sx={{ backgroundColor: "white" }}
                        value={term}
                        onChange={(e) =>
                          setValue(
                            `companyPaymentTerms.${index}`,
                            e.target.value
                          )
                        }
                      />
                    </FormControl>
                  </div>
                ))}

                {errors.companyPaymentTerms?.message && (
                  <FormHelperText>
                    {errors.companyPaymentTerms?.message}
                  </FormHelperText>
                )}

                <Button
                  variant="linkz-outlined-primary"
                  sx={{ height: 40, maxHeight: 40, width: 150, maxWidth: 150 }}
                  onClick={() => {
                    setValue("companyPaymentTerms", [
                      ...(watch("companyPaymentTerms") ?? []),
                      "",
                    ]);
                  }}
                >
                  {t("profile.business.addPaymentTrmBtn")}
                </Button>

                <Stack gap={6} direction="column">
                  <Typography startDecorator={<Puzzle />} fontWeight={"lg"}>
                    {t("profile.business.taxSettings.title")}
                  </Typography>

                  <Stack
                    gap={"20px"}
                    padding={4}
                    borderRadius={8}
                    border={"1px solid #D0D5DD"}
                    boxShadow={"0px 1px 3px 0px #1018281A"}
                  >
                    {watch("companyCountry") && taxOptions.length > 0 && (
                      <Stack
                        padding={"12px"}
                        gap="10px"
                        borderRadius={"8px"}
                        flexDirection={"row"}
                        className="bg-[#CFE5DA]"
                      >
                        <ExclamationCircleIcon width={24} height={24} />
                        <Typography>
                          You can add more than one tax options
                        </Typography>
                      </Stack>
                    )}

                    {!watch("companyCountry") ? (
                      <Typography className="w-full" textAlign={"center"}>
                        {t("profile.business.taxSettings.countryPrompt")}
                      </Typography>
                    ) : watch("companyCountry") && taxOptions.length === 0 ? (
                      <Typography className="w-full text-center">
                        {t("profile.business.taxSettings.adminPrompt")}
                      </Typography>
                    ) : (
                      <Stack
                        rowGap={1}
                        flexWrap={"wrap"}
                        direction={{ xs: "column", lg: "row" }}
                      >
                        {taxOptions.map((tax, index) => (
                          <TaxItem
                            defaultTaxId={defaultTaxId}
                            index={index}
                            key={tax.id}
                            tax={tax}
                            selectedTaxes={selectedTaxes}
                            setSelectedTaxes={setSelectedTaxes}
                            setDefaultTaxId={setDefaultTaxId}
                          />
                        ))}
                      </Stack>
                    )}
                  </Stack>
                </Stack>
              </Box>
            </If>

            {/* payment kyc */}
            <If condition={currentStep === 3}>
              <Box
                sx={{
                  borderWidth: "1px",
                  borderColor: theme.palette.neutral[300],
                  borderRadius: "8px",
                  py: "32px",
                  px: "24px",
                  display: "flex",
                  flexDirection: "column",
                  gap: "32px",
                }}
              >
                <Stack direction={"column"}>
                  <Typography
                    level="text-xl2-bold"
                    sx={{
                      color:
                        theme.palette.others.onboarding_create_account_font,
                    }}
                  >
                    {t("upgradeAccount.form.steps.3.title")}
                  </Typography>
                  <Typography level="text-xl-medium">
                    {t("upgradeAccount.form.steps.3.subtitle")}
                  </Typography>
                </Stack>

                <Stack gap={2} width={{ xs: "90vw", lg: "100%" }}>
                  <Typography
                    fontWeight={"lg"}
                    fontSize={"lg"}
                    textColor="neutral.900"
                  >
                    {t("profileKYC.title")}
                  </Typography>
                  <Typography
                    fontWeight={"md"}
                    fontSize={"md"}
                    textColor="neutral.900"
                  >
                    {t("profileKYC.subTitle")}
                  </Typography>

                  <Card
                    sx={{
                      paddingLeft: "2rem",
                      paddingRight: "2rem",
                      paddingBottom: "2rem",
                    }}
                  >
                    <Stack
                      gap={2}
                      justifyContent={"center"}
                      alignItems={"center"}
                    >
                      <Typography>
                        {t("profileKYC.scanCompanyFile")}[
                        <Typography fontWeight={"lg"}>
                          {t("profileKYC.scanCompanyFileName")}
                        </Typography>
                        ] & [
                        <Typography fontWeight={"lg"}>
                          {t(
                            `profileKYC.forms.${BusinessSessionHelper.Business()?.countryCode.toLowerCase()}.scanCompanyFileKTP`
                          )}
                        </Typography>
                        ].
                      </Typography>
                      {!companyProfileFile ? (
                        <Card
                          className="drop-zone-business-profile-file"
                          sx={{
                            width: "100%",
                            height: "160px",
                            border: "1px dashed #62A583",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          <Stack
                            gap={2}
                            justifyContent={"center"}
                            alignItems={"center"}
                          >
                            <Button
                              onClick={() =>
                                document
                                  .getElementById("company-profile-file")
                                  ?.click()
                              }
                              startDecorator={
                                <ArrowUpTrayIcon width={20} height={20} />
                              }
                              variant="plain"
                              color="primary"
                            >
                              Upload Here
                            </Button>
                            <Typography>
                              {t("profileKYC.dragFileDesc")}
                            </Typography>
                            <input
                              id="company-profile-file"
                              type="file"
                              accept="image/*, application/pdf"
                              style={{ display: "none" }}
                              onChange={(e) =>
                                onFileChange(e, (file: File) => {
                                  setCompanyProfileFile(file);
                                  setError("companyProfileFileUrl", {});
                                })
                              }
                            />
                          </Stack>
                        </Card>
                      ) : (
                        <Card sx={{ width: "100%" }}>
                          <Stack
                            direction={"row"}
                            gap={2}
                            justifyContent={"start"}
                            alignItems={"center"}
                          >
                            <img src="/images/imgIcon.svg" />
                            <Stack flex={1}>
                              <Typography fontWeight={"lg"} fontSize={"md"}>
                                {companyProfileFile?.name}
                              </Typography>
                              <Typography fontSize={"sm"}>
                                {(
                                  companyProfileFile?.size /
                                  (1024 * 1024)
                                ).toFixed(2)}{" "}
                                MB
                              </Typography>
                            </Stack>
                            <IconButton
                              onClick={() => setCompanyProfileFile(null)}
                            >
                              <TrashIcon
                                color={
                                  theme.colorSchemes.light.palette.danger[500]
                                }
                              />
                            </IconButton>
                          </Stack>
                        </Card>
                      )}
                      {errors.companyProfileFileUrl && (
                        <FormHelperText sx={{ color: "red" }}>
                          {errors.companyProfileFileUrl.message}
                        </FormHelperText>
                      )}
                    </Stack>
                  </Card>
                  <Stack gap={2} direction={{ xs: "column", md: "row" }}>
                    <div style={{ flex: 1 }}>
                      <FormLabel>{t("profileKYC.formLabel1")}</FormLabel>
                      <FormControl
                        sx={{ flex: 1 }}
                        error={
                          !!errors.estimatedAnualIncome ||
                          !!errors.estimatedAnualIncomeCurrency
                        }
                      >
                        <Select
                          sx={{ ...animateSelectStyle }}
                          indicator={<KeyboardArrowDown />}
                          defaultValue={ESTIMATED_ANUAL_REVENUE[0]}
                          onChange={(_, value) => {
                            setValue("estimatedAnualIncome", value ?? "");
                            setValue(
                              "estimatedAnualIncomeCurrency",
                              getValues("companyCurrency") ?? "IDR"
                            );
                          }}
                          startDecorator={
                            <React.Fragment>
                              <Select
                                disabled
                                variant="plain"
                                defaultValue={
                                  getCountryInfo(
                                    BusinessSessionHelper.Business()
                                      ?.companyCountry ?? "IDR",
                                    "LOCALE"
                                  )?.currency
                                }
                                indicator={<></>}
                                onChange={(_, value) =>
                                  setValue(
                                    "estimatedAnualIncomeCurrency",
                                    value ?? "IDR"
                                  )
                                }
                                sx={{ ...animateSelectStyle, paddingLeft: 0 }}
                              >
                                {currencyList.map((c) => {
                                  return (
                                    <Option id={c} value={c}>
                                      {c}
                                    </Option>
                                  );
                                })}
                              </Select>
                              <Divider orientation="vertical" />
                            </React.Fragment>
                          }
                        >
                          {ESTIMATED_ANUAL_REVENUE.slice(1).map(
                            (option: any) => (
                              <Option key={option} value={option}>
                                {option}
                              </Option>
                            )
                          )}
                        </Select>
                      </FormControl>
                      {(errors.estimatedAnualIncome ||
                        errors.estimatedAnualIncomeCurrency) && (
                        <FormHelperText sx={{ color: "red" }}>
                          {errors.estimatedAnualIncome?.message ||
                            errors.estimatedAnualIncomeCurrency?.message}
                        </FormHelperText>
                      )}
                    </div>
                    <div style={{ flex: 1 }}>
                      <FormLabel>{t("profileKYC.formLabel2")}</FormLabel>
                      <FormControl
                        sx={{ flex: 1 }}
                        error={
                          !!errors.averageValuePerTransaction ||
                          !!errors.averageValuePerTransactionCurrency
                        }
                      >
                        <Select
                          sx={{ ...animateSelectStyle }}
                          indicator={<KeyboardArrowDown />}
                          defaultValue={AVERAGE_VALUE_OF_TRANSACTIONS[0]}
                          onChange={(_, value) => {
                            setValue("averageValuePerTransaction", value ?? "");
                            setValue(
                              "averageValuePerTransactionCurrency",
                              getValues("companyCurrency") ?? "IDR"
                            );
                          }}
                          startDecorator={
                            <React.Fragment>
                              <Select
                                disabled
                                variant="plain"
                                defaultValue={
                                  getCountryInfo(
                                    BusinessSessionHelper.Business()
                                      ?.companyCountry ?? "IDR",
                                    "LOCALE"
                                  )?.currency
                                }
                                indicator={<></>}
                                onChange={(_, value) =>
                                  setValue(
                                    "averageValuePerTransactionCurrency",
                                    value ?? "IDR"
                                  )
                                }
                                sx={{ ...animateSelectStyle, paddingLeft: 0 }}
                              >
                                {currencyList.map((c) => {
                                  return <Option value={c}>{c}</Option>;
                                })}
                              </Select>
                              <Divider orientation="vertical" />
                            </React.Fragment>
                          }
                        >
                          {AVERAGE_VALUE_OF_TRANSACTIONS.slice(1).map(
                            (option: any) => (
                              <Option key={option} value={option}>
                                {option}
                              </Option>
                            )
                          )}
                        </Select>
                      </FormControl>
                      {(errors.averageValuePerTransaction ||
                        errors.averageValuePerTransactionCurrency) && (
                        <FormHelperText sx={{ color: "red" }}>
                          {errors.averageValuePerTransaction?.message ||
                            errors.averageValuePerTransactionCurrency?.message}
                        </FormHelperText>
                      )}
                    </div>
                  </Stack>
                  <div>
                    <FormLabel>{t("profileKYC.formLabel3")}</FormLabel>
                    <FormControl error={!!errors.bankName}>
                      <Select
                        indicator={<KeyboardArrowDown />}
                        sx={{ ...animateSelectStyle }}
                        placeholder={t("profileKYC.formLabel3Placeholder")}
                        defaultValue={null}
                        onChange={(_, value) =>
                          setValue("bankName", (value as any)?.code ?? "")
                        }
                        startDecorator={
                          <React.Fragment>
                            {(BANK_OPTIONS.find(
                              (option: any) => option.code === watch("bankName")
                            )?.icon && (
                              <Card
                                sx={{
                                  backgroundImage: `url(${
                                    BANK_OPTIONS.find(
                                      (option: any) =>
                                        option.code === watch("bankName")
                                    )?.icon ?? BANK_OPTIONS[0].icon
                                  })`,
                                  backgroundSize: "cover",
                                  borderColor: "transparent",
                                  marginRight: "0.5rem",
                                }}
                              />
                            )) || (
                              <div style={{ marginRight: "0.5rem" }}>
                                <CurrencyDolar />
                              </div>
                            )}
                            <Divider orientation="vertical" />
                          </React.Fragment>
                        }
                      >
                        {BANK_OPTIONS.slice(1).map((option: any) => (
                          <Option key={option.code} value={option}>
                            {option.name}
                          </Option>
                        ))}
                      </Select>
                    </FormControl>
                    {errors.bankName && (
                      <FormHelperText sx={{ color: "red" }}>
                        {errors.bankName?.message}
                      </FormHelperText>
                    )}
                  </div>
                  <div>
                    <FormLabel>{t("profileKYC.formLabel4")}</FormLabel>
                    <FormControl error={!!errors.accountNumber}>
                      <Input type="number" {...register("accountNumber")} />
                    </FormControl>
                    {errors.accountNumber && (
                      <FormHelperText sx={{ color: "red" }}>
                        {errors.accountNumber?.message}
                      </FormHelperText>
                    )}
                  </div>
                  <Card
                    sx={{
                      paddingLeft: "2rem",
                      paddingRight: "2rem",
                      paddingBottom: "2rem",
                    }}
                  >
                    <Stack
                      gap={2}
                      justifyContent={"center"}
                      alignItems={"center"}
                    >
                      <Stack
                        width={"100%"}
                        direction={"column"}
                        gap={2}
                        justifyContent={"start"}
                        alignItems={"start"}
                      >
                        <Typography>
                          {t("profileKYC.scanPersonalIDDesc")}
                        </Typography>
                        <Stack
                          width={"100%"}
                          direction={"row"}
                          gap={3}
                          justifyContent={"center"}
                          alignItems={"center"}
                        >
                          <Stack
                            gap={1}
                            justifyContent={"center"}
                            alignItems={"center"}
                          >
                            <img
                              src={`/images/correct${
                                BusinessSessionHelper.Business()?.countryCode
                              }.svg`}
                            />
                            <Typography
                              startDecorator={
                                <CheckIcon
                                  color="#489C5E"
                                  width={30}
                                  height={30}
                                />
                              }
                              fontSize={"lg"}
                              fontWeight={"lg"}
                            >
                              {t("profileKYC.personalIDCorrectTitle")}
                            </Typography>
                            <Typography>
                              {t("profileKYC.personalIDCorrectDesc")}
                            </Typography>
                          </Stack>
                          <Stack
                            gap={1}
                            justifyContent={"center"}
                            alignItems={"center"}
                          >
                            <img
                              src={`/images/inCorrect${
                                BusinessSessionHelper.Business()?.countryCode
                              }.svg`}
                            />
                            <Typography
                              startDecorator={
                                <XMarkIcon
                                  color="darkred"
                                  width={30}
                                  height={30}
                                />
                              }
                              fontSize={"lg"}
                              fontWeight={"lg"}
                            >
                              {t("profileKYC.personalIDInCorrectTitle")}
                            </Typography>
                            <Typography>
                              {t("profileKYC.personalIDInCorrectDesc")}
                            </Typography>
                          </Stack>
                        </Stack>
                      </Stack>
                      {!personalIDFile ? (
                        <Card
                          className="drop-zone-personal-id-file"
                          sx={{
                            width: "100%",
                            height: "160px",
                            border: "1px dashed #62A583",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          <Stack
                            gap={2}
                            justifyContent={"center"}
                            alignItems={"center"}
                          >
                            <Button
                              onClick={() =>
                                document
                                  .getElementById("personal-id-file")
                                  ?.click()
                              }
                              startDecorator={
                                <ArrowUpTrayIcon width={20} height={20} />
                              }
                              variant="plain"
                              color="primary"
                            >
                              Upload Here
                            </Button>
                            <Typography>
                              {t("profileKYC.dragFileDesc")}
                            </Typography>
                            <input
                              id="personal-id-file"
                              type="file"
                              accept="image/*, application/pdf"
                              style={{ display: "none" }}
                              onChange={(e) =>
                                onFileChange(e, (file: File) => {
                                  setPersonalIDFile(file);
                                  setError("personalIdFileUrl", {});
                                })
                              }
                            />
                          </Stack>
                        </Card>
                      ) : (
                        <Card sx={{ width: "100%" }}>
                          <Stack
                            direction={"row"}
                            gap={2}
                            justifyContent={"start"}
                            alignItems={"center"}
                          >
                            <img src="/images/imgIcon.svg" />
                            <Stack flex={1}>
                              <Typography fontWeight={"lg"} fontSize={"md"}>
                                {personalIDFile?.name}
                              </Typography>
                              <Typography fontSize={"sm"}>
                                {(personalIDFile?.size / (1024 * 1024)).toFixed(
                                  2
                                )}{" "}
                                MB
                              </Typography>
                            </Stack>
                            <IconButton onClick={() => setPersonalIDFile(null)}>
                              <TrashIcon
                                color={
                                  theme.colorSchemes.light.palette.danger[500]
                                }
                              />
                            </IconButton>
                          </Stack>
                        </Card>
                      )}
                      {errors.personalIdFileUrl && (
                        <FormHelperText sx={{ color: "red" }}>
                          {errors.personalIdFileUrl?.message}
                        </FormHelperText>
                      )}
                    </Stack>
                  </Card>
                  <p>
                    <Typography
                      startDecorator={
                        <Checkbox
                          onChange={(e) => setIsAcceptTerms(e.target.checked)}
                        />
                      }
                    >
                      <span>
                        {t("profileKYC.terms")}
                        <span
                          className={"text-[#499873] cursor-pointer"}
                          style={{ fontWeight: "bold", cursor: "pointer" }}
                        >
                          <a
                            href={`${import.meta.env.VITE_TC_URL ?? "#"}`}
                            target="_blank"
                          >
                            {t("landing.signup.terms")}
                          </a>
                        </span>{" "}
                        &
                        <span
                          className={"text-[#499873] cursor-pointer"}
                          style={{ fontWeight: "bold", cursor: "pointer" }}
                        >
                          <a
                            href={`${import.meta.env.VITE_PP_URL ?? "#"}`}
                            target="_blank"
                          >
                            {t("landing.signup.privacy")}
                          </a>
                        </span>
                      </span>
                    </Typography>
                  </p>
                </Stack>
              </Box>
            </If>

            {/* buttons */}
            <Stack
              direction={{ xs: "column-reverse", md: "row" }}
              sx={{
                position: "relative",
                justifyContent: { md: "space-between" },
                alignItems: "center",
                width: "100%",
                py: { xs: "16px", md: "24px" },
                px: 0,
                gap: { xs: "8px", md: "16px" },
              }}
            >
              <Divider
                sx={{
                  width: "100dvw",
                  position: "absolute",
                  display: { xs: "none", lg: "block" },
                  top: 0,
                  left: "-304px",
                  backgroundColor: theme.palette.others.disabled,
                }}
              />

              <IfElse
                condition={currentStep === 1}
                ifBlock={
                  <Button
                    id="upgrade-cancel-button"
                    variant="linkz-outlined-primary"
                    onClick={() => {
                      navigate("/client/upgrade-account");
                    }}
                    sx={{
                      my: "auto",
                      minWidth: { xs: "100%", lg: "152px" },
                    }}
                  >
                    {t("commons.cancelBtn")}
                  </Button>
                }
                elseBlock={
                  <IfElse
                    condition={currentStep === 3}
                    ifBlock={
                      <Button
                        id="upgrade-skip-button"
                        variant="linkz-outlined-primary"
                        onClick={() => setShowConfirmModal(true)}
                        sx={{
                          my: "auto",
                          minWidth: { xs: "100%", lg: "152px" },
                        }}
                      >
                        {t("commons.skipBtn")}
                      </Button>
                    }
                    elseBlock={
                      <Button
                        id="upgrade-prev-button"
                        variant="linkz-outlined-primary"
                        onClick={handleGotoPrev}
                        sx={{
                          my: "auto",
                          minWidth: { xs: "100%", lg: "152px" },
                        }}
                        startDecorator={
                          <ArrowLeftIcon width={20} height={20} />
                        }
                      >
                        {t("commons.prevBtn")}
                      </Button>
                    }
                  />
                }
              />

              <If condition={currentStep < 3}>
                <Button
                  id="upgrade-next-button"
                  sx={{
                    my: "auto",
                    minWidth: { xs: "100%", md: "200px" },
                  }}
                  disabled={!isStepValid}
                  endDecorator={<ArrowRightIcon width={20} height={20} />}
                  onClick={handleGoToNext}
                >
                  {t("commons.nextBtn")}
                </Button>
              </If>

              <If condition={currentStep === 3}>
                <Button
                  id="upgrade-submit-desktop"
                  sx={{
                    my: "auto",
                    minWidth: { xs: "100%", md: "100px" },
                  }}
                  disabled={!isStepValid || !isAcceptTerms}
                  startDecorator={<CheckIcon width={20} />}
                  onClick={() => {
                    setWithKyc(true);
                    setShowConfirmModal(true);
                  }}
                >
                  {t("commons.submitBtn")}
                </Button>
              </If>
            </Stack>
          </Stack>

          {/* modals */}
          <LoadingModal isLoading={isLoading} />
          <ConfirmUpgradeModal
            show={showConfirmModal}
            onConfirm={handleSubmit(onSubmit)}
            onClose={() => {
              setShowConfirmModal(false);
            }}
          />
        </form>
      </FormProvider>
    </Sheet>
  );
};

const CustomImagePolicy = ({ file }: { file: File | null }) => {
  const [imageSize, _setImageSize] = useState<number[]>([0, 0]);
  const { t } = useTranslation();

  const isLessThenEq2MB = () => {
    return (file?.size || 0) <= 2 * 1024 * 1024;
  };

  const isImage = () => {
    return (file?.type || "").startsWith("image");
  };

  const isRecomendedSize = () => {
    return (file && imageSize[0] === 2000 && imageSize[1] === 2000) || false;
  };

  return (
    <ul>
      <li>
        <Stack direction={"row"} alignItems={"center"} gap={1}>
          <PolicyBullet isActive={isLessThenEq2MB() && isImage()} />
          <Typography
            fontSize={14}
            textColor={
              isLessThenEq2MB() && isImage() ? "primary.400" : "neutral.500"
            }
          >
            {t("imagePolicy.requirement1")}
          </Typography>
        </Stack>
      </li>
      <li>
        <Stack direction={"row"} alignItems={"center"} gap={1}>
          <PolicyBullet isActive={isRecomendedSize()} />
          <Typography
            fontSize={14}
            textColor={isRecomendedSize() ? "primary.400" : "neutral.500"}
          >
            {t("imagePolicy.requirement2")}
          </Typography>
        </Stack>
      </li>
    </ul>
  );
};

export default UpgradeForm;
